import * as React from 'react';
import { useLocation, useRoute } from 'wouter';
import { useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { CmdLoader } from '@commander-services/gui-components';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import OnlineHelp from '../../OnlineHelp';
import { CmdTable } from '../../CmdTable/CmdTable';
import { ICmdTableColumn, ICmdTableFilter } from '../../CmdTable/types';
import { REQUIRED } from '../../CmdTable/constants';
import * as CmdTableService from '../../CmdTable/CmdTableService';
import {
  SUPPLIER_ACTIVE_KEY,
  SUPPLIER_CUSTOMER_ID_KEY,
  SUPPLIER_DELETED_AT_KEY,
  SUPPLIER_DETAIL_KEY,
  SUPPLIER_NAME_KEY,
  SUPPLIER_TABLE_KEY,
  SUPPLIER_TABLE_SETTINGS,
  TableSettingsKeys,
} from './constants';
import CmdTableSettings from '../../CmdTable/settings/CmdTableSettings';
import FilterChips from '../../CmdTable/FilterChips/FilterChips';
import { TSupplierRecords, TSupplierResponse } from './interfaces';
import useUrlQueryParams from '../../../hooks/useUrlQueryParams';
import useSuppliers from './useSuppliers';
import SuppliersForm from './SuppliersForm';
import { URL_SUPPLIERS } from '../../../router/constants';
import PermissionsService, { PERMISSION_SUPPLIER } from '../../../Services/PermissionsService';
import { customersAtom, selectedCustomersAtom } from '../../../store/recoil/customers';
import {
  columnsAtom,
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
  tableFiltersAtomFamily,
} from '../../CmdTable/CmdTableState';
import { getTableSettings } from '../../CmdTable/CmdTableService';
import useAnalytics from '../../../hooks/useAnalytics';
import PageHeader from '../../PageHeader';

const SUPPLIERS_URL = 'suppliers';

export default function Codebooks() {
  const { formatMessage: f } = useIntl();
  const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
  const [customerId, setCustomerId] = React.useState<number | null>(null);
  const [pathname, navigate] = useLocation();
  const [, params] = useRoute(`/${SUPPLIERS_URL}/edit/:id`);
  const { setDocumentTitle } = useDocumentTitle();
  const [id, setId] = React.useState<number | null>(null);
  const tableFiltersKey = useRecoilValue(tableFiltersAtomFamily(SUPPLIER_TABLE_KEY));
  const setColumnsTable = useSetRecoilState<ICmdTableColumn[]>(columnsAtom);
  const customer = useRecoilValue(customersAtom);
  const queryClient = useQueryClient();
  const filterForRequest = useRecoilValue<ICmdTableFilter>(
    filterForRequestAtomFamily(SUPPLIER_TABLE_KEY)
  );
  const sortFilter = useRecoilValue<{ [key: string]: string }>(
    sortFilterAtomFamily(SUPPLIER_TABLE_KEY)
  );
  const query = useUrlQueryParams();
  const customerIdUrl = query.get('customerId');
  const { columns, columnOrder, columnVisibility } = useSuppliers();
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const { trackEvent } = useAnalytics();

  const handleClose = React.useCallback(() => {
    navigate(`${URL_SUPPLIERS}`);
    setIsModalOpened(false);
  }, [navigate]);

  const updateTable = () => {
    queryClient.invalidateQueries({
      queryKey: [SUPPLIER_TABLE_KEY, filterForRequest, sortFilter],
    });
  };

  const handlePathData = (documentTitle: string, supplierId?: number, customerValue?: number) => {
    setIsModalOpened(true);
    setId(supplierId || null);
    setCustomerId(customerValue || null);
    setDocumentTitle(documentTitle);
  };

  React.useEffect(() => {
    if (pathname === `${URL_SUPPLIERS}`) {
      const documentTitle = `${f({
        id: 'menu.suppliersOverview',
      })} - ${f({ id: 'menu.costs' })}`;
      setDocumentTitle(documentTitle);
    }
    if (pathname === `${URL_SUPPLIERS}/add`) {
      const documentTitle = `${f({ id: 'table.add' })} - ${f({
        id: 'menu.suppliersOverview',
      })} - ${f({ id: 'menu.costs' })}`;
      handlePathData(documentTitle);
    } else if (params) {
      const documentTitle = `${f({ id: 'table.detail' })} - ${f({
        id: 'menu.suppliersOverview',
      })} - ${f({ id: 'menu.costs' })}`;
      handlePathData(documentTitle, Number(params.id), Number(customerIdUrl));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleClose, navigate, pathname]);

  const handleSetOrderColumns = (
    newOrderColumns: { name: string; hidden: boolean; visibility: string; sort: boolean }[]
  ) => {
    const tempOrder = newOrderColumns.map((item) => item.name);

    const tempTableSettingOrder: ICmdTableColumn[] = [];

    newOrderColumns.map((item) => {
      if (customer.length === 1 && item.name === SUPPLIER_CUSTOMER_ID_KEY) {
        tempTableSettingOrder.push({
          key: item.name,
          default: item.visibility === REQUIRED,
          hidden: true,
          system: true,
        });
      } else {
        tempTableSettingOrder.push({
          key: item.name,
          default: item.visibility === REQUIRED,
          hidden: item.hidden,
        });
      }
    });

    tempTableSettingOrder.push({ key: 'actions', hidden: false, default: false });

    const orderColumns = CmdTableService.arrayOrder(
      [...tempTableSettingOrder],
      [...tempOrder],
      'key'
    );

    setColumnsTable(orderColumns);
  };

  const getSettings = async () => {
    const settings = await getTableSettings(SUPPLIER_TABLE_KEY);
    if (settings) {
      handleSetOrderColumns(settings.columns);

      return settings;
    }
    return false;
  };

  const results = useQueries({
    queries: [
      {
        queryKey: ['cmdTableSettings'],
        queryFn: () => getSettings(),
        staleTime: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const { isLoading: isLoadingSettings } = results[0];

  const handleAddButton = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigate(`${URL_SUPPLIERS}/add`);
    trackEvent(['supplier table', 'click', 'Add supplier']);
  };

  return (
    <div>
      <PageHeader
        title={`${f({ id: 'menu.costs' })} / ${f({ id: 'menu.suppliersOverview' })}`}
        onlineHelpTopic="suppliers"
      />
      <div className="w-page mt-8">
        <div className="w-page-content">
          <div className="container-fluid">
            <section className="basic-box border-top-thin border-top-gray">
              <div className="basic-box__content">
                <div className="row no-gutters mb-2">
                  <div className="col-12 col-md-auto mr-md-2 mb-2">
                    {PermissionsService.hasAccess(
                      PERMISSION_SUPPLIER,
                      ['create'],
                      selectedCustomers
                    ) && (
                      <a
                        href="#"
                        data-cy="table-record-addBtn"
                        title={f({ id: 'table.addBtn' })}
                        className="e-button e-button--gray"
                        data-id="addAlarm"
                        // style="margin-right: 7px;"
                        onClick={handleAddButton}
                      >
                        {f({ id: 'table.addBtn' })}
                      </a>
                    )}
                    {/* </div> */}
                  </div>
                  <div className="col col-md" />
                  <div className="col-auto col-md-12 mt-2 ml-2 mt-md-0 ml-md-0 col-lg-auto order-5 order-lg-4 mt-lg-2 mr-2" />
                  <div className="col-auto order-4 order-lg-5">
                    <CmdTableSettings
                      tableId={`${SUPPLIER_TABLE_KEY}s`}
                      // actionColumnEnabled={true}
                      tableName={SUPPLIER_TABLE_SETTINGS}
                      sort={{ column: SUPPLIER_NAME_KEY, direction: 'desc' }}
                      translationKeys={TableSettingsKeys}
                    />
                  </div>
                </div>
                <div className="d-flex flex-wrap mb-2">
                  {tableFiltersKey &&
                    tableFiltersKey.map((filterId) => (
                      <FilterChips key={filterId} id={filterId} tableName={SUPPLIER_TABLE_KEY} />
                    ))}
                </div>
                {isLoadingSettings && <CmdLoader inContent={true} />}
                <CmdTable<TSupplierResponse, TSupplierRecords>
                  name={SUPPLIER_TABLE_KEY}
                  stickyColumns={[SUPPLIER_DETAIL_KEY, SUPPLIER_DELETED_AT_KEY]}
                  lockedColumns={[SUPPLIER_ACTIVE_KEY]}
                  columns={columns}
                  columnOrder={columnOrder}
                  columnVisibility={columnVisibility}
                />
              </div>
            </section>
            {isModalOpened && id && (
              <SuppliersForm
                isEdited={true}
                closeCallback={handleClose}
                selectedId={id}
                updateTable={updateTable}
                customerIds={customerId}
              />
            )}
            {isModalOpened && !id && (
              <SuppliersForm
                isEdited={false}
                closeCallback={handleClose}
                selectedId={null}
                updateTable={updateTable}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
